import { useQuery } from '@tanstack/react-query';
import type {
  AppointmentWithRelations,
  TaskInboxSearchRequest,
  TaskWithRelations,
} from '@vagusx/clients';
import { getTaskClient, getAppointmentClient } from '@/lib/api-clients';
import { queryKeys } from '@/lib/query-keys';
import { CACHE_TIME_10_MINS } from '@/lib/query-client';

/**
 * A "referral" isn't its own entity — it's a task carrying one of these
 * classification labels, backed by a referral document. The label filter on
 * `searchInbox` uses array-overlap (`&&`) semantics, so passing both matches a
 * task that has EITHER label (OR), which is what we want.
 */
export const REFERRAL_LABELS = ['referral', 'patient_referral'] as const;

/**
 * Default scope for the Referrals worklist: open / unscheduled only. Completed
 * (booked) and cancelled referrals drop off so the page stays a list of
 * referrals still needing an appointment.
 */
export const OPEN_REFERRAL_STATUSES = ['open', 'in_progress'] as const;

/**
 * A referral task can resolve to a referral document only when it points at a
 * document or email source. Single source of truth for gating the schedule
 * action and the per-row document / availability lookups.
 */
export function canResolveReferralDocument(task: TaskWithRelations): boolean {
  return !!task.objectId && (task.objectType === 'document' || task.objectType === 'email');
}

export interface ReferralTaskListParams {
  limit: number;
  offset: number;
  searchTerm?: string;
  sortBy?: 'createdAt' | 'priority' | 'status';
  sortOrder?: 'asc' | 'desc';
}

/**
 * List referral tasks for the quick-view. Reuses the task inbox search endpoint
 * with a fixed label + status filter — no new backend endpoint. Auto-refreshes
 * on the same cadence as the task inbox.
 */
export function useReferralTasks(params: ReferralTaskListParams) {
  const request: TaskInboxSearchRequest = {
    ...params,
    labels: [...REFERRAL_LABELS],
    status: [...OPEN_REFERRAL_STATUSES],
  };
  return useQuery({
    queryKey: queryKeys.referrals.list(request),
    queryFn: () => getTaskClient().searchInbox(request),
    refetchInterval: 5 * 60 * 1000,
  });
}

/**
 * Referral lifecycle state shown in the Status column. Kept intentionally
 * small for now — a referral is either still waiting on a booking or has one.
 * Later states (synced-to-EHR, patient-visited, report-sent) are a follow-up
 * and will derive from the appointment's sync/status fields + new tracking.
 */
export type ReferralStatus = 'waiting_to_schedule' | 'scheduled';

export const REFERRAL_STATUS_LABEL: Record<ReferralStatus, string> = {
  waiting_to_schedule: 'Waiting to schedule',
  scheduled: 'Scheduled',
};

/** Presence of a linked appointment is the whole signal for now. */
export function referralStatusForTask(task: TaskWithRelations): ReferralStatus {
  return task.appointmentId ? 'scheduled' : 'waiting_to_schedule';
}

/**
 * The actual booked appointment for a referral, when one exists. A referral
 * task links to its appointment via `appointmentId`, set when a scheduling call
 * books (see the booking flow in apps/api). Until then the referral is "waiting
 * to schedule" and the actual-appointment columns render blank. Only fetches
 * when a link exists, so unscheduled rows issue no request.
 */
export function useReferralAppointment(task: TaskWithRelations): {
  appointment: AppointmentWithRelations | null;
  isLoading: boolean;
  status: ReferralStatus;
} {
  const appointmentId = task.appointmentId ?? undefined;
  const query = useQuery({
    queryKey: queryKeys.appointments.detail(appointmentId ?? ''),
    queryFn: () => getAppointmentClient().getById(appointmentId!),
    enabled: !!appointmentId,
    staleTime: CACHE_TIME_10_MINS,
  });
  return {
    appointment: query.data ?? null,
    isLoading: !!appointmentId && query.isLoading,
    status: referralStatusForTask(task),
  };
}

// ── Pure presentation helpers ──────────────────────────────────────────────

export type PatientType = 'New' | 'Existing';

/**
 * Map the task's classification labels to the new/existing-patient column.
 * Returns null when neither signal is present (rendered as "—").
 */
export function formatPatientType(labels: string[] | undefined): PatientType | null {
  if (!labels) return null;
  if (labels.includes('new_patient')) return 'New';
  if (labels.includes('existing_patient')) return 'Existing';
  return null;
}

export type ReferralUrgency = 'Stat' | 'Urgent' | 'Regular';

/**
 * Resolve the Regular | Urgent | Stat column. The referral document's `urgency`
 * field is the primary source ('stat' | 'urgent' | 'routine'); we fall back to
 * the task's urgency labels / priority when no document urgency is set so the
 * column is still meaningful before extraction completes.
 */
export function formatUrgency(
  documentUrgency: string | null | undefined,
  labels: string[] | undefined,
  priority: string | undefined
): ReferralUrgency {
  const u = documentUrgency?.trim().toLowerCase();
  if (u === 'stat') return 'Stat';
  if (u === 'urgent') return 'Urgent';
  if (u === 'routine') return 'Regular';

  if (labels?.includes('stat')) return 'Stat';
  if (labels?.includes('urgent') || priority === 'urgent') return 'Urgent';
  return 'Regular';
}
